import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../../components/AppToast";
import CommonDataTable from "../../../components/CommonDataTable";
import { DotLoaderMark } from "../../../components/Loader";
import { fetchSkuMaster, getSkuDetailsById } from "../../../services/item/item";
import CreateSkuForm from "./CreateSkuForm/CreateSkuForm";
import "./skuMaster.css";

const SKU_FORM_BREADCRUMB = {
  create: "New SKU",
  view: "View SKU",
  edit: "Edit SKU",
};

const SKU_FILTER_FIELD_MAP = {
  __actions: null,
  statusUi: null,
  skuCode: "header.skuId",
  skuName: "header.description",
  category: "header.skugroup",
  itemType: "header.skugroup2",
  brand: "header.owner",
  inventoryUom: "header.uom",
};

function mapSkuUiFiltersToApi(filters = []) {
  if (!Array.isArray(filters)) return [];
  return filters
    .map((f) => {
      if (!f || !f.columnId) return null;
      const apiField = Object.prototype.hasOwnProperty.call(SKU_FILTER_FIELD_MAP, f.columnId)
        ? SKU_FILTER_FIELD_MAP[f.columnId]
        : f.columnId;
      if (apiField == null) return null;
      return {
        columnId: apiField,
        operator: f.operator,
        value: f.value,
      };
    })
    .filter(Boolean);
}

function mergeOptionValues(prev, rows, key) {
  const values = new Set(prev);
  rows.forEach((row) => {
    const value = row?.[key];
    if (value && value !== "—") values.add(value);
  });
  return [...values].sort();
}

export default function SkuList() {
  const [tableRows, setTableRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  /** `null` = list; otherwise `{ mode: "create" | "view" | "edit", record }` renders the SKU form. */
  const [skuForm, setSkuForm] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [blockedIds] = useState(() => new Set());
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const isCreating = !!skuForm;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterItemType, setFilterItemType] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [itemTypeOptions, setItemTypeOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const headerFilters = useMemo(() => {
    const filters = [];
    const q = search.trim();
    if (q) {
      filters.push({ columnId: "header.skuId", operator: "contains", value: q });
    }
    if (filterCategory) {
      filters.push({ columnId: "header.skugroup", operator: "equals", value: filterCategory });
    }
    if (filterItemType) {
      filters.push({ columnId: "header.skugroup2", operator: "equals", value: filterItemType });
    }
    if (filterBrand) {
      filters.push({ columnId: "header.owner", operator: "equals", value: filterBrand });
    }
    return filters;
  }, [search, filterCategory, filterItemType, filterBrand]);

  const requestFilters = useMemo(
    () => [...mapSkuUiFiltersToApi(appliedFilters), ...headerFilters],
    [appliedFilters, headerFilters]
  );

  const loadList = useCallback(async (pageNo, pageSizeNo, filters = [], { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchSkuMaster(pageNo, pageSizeNo, filters);
      const list = Array.isArray(res) ? res : res?.data || res?.list || res?.results || [];
      const formatted = list.map((item) => {
        const h = item.header || item;
        const id = item._id || h._id || h.skuId;
        return {
          id,
          skuCode: h.skuId ?? item.skuId ?? "",
          skuName: h.description ?? item.description ?? "",
          category: h.skugroup ?? h.stockCategory ?? h.department ?? "—",
          itemType: h.skugroup2 ?? h.itemGroup ?? "—",
          brand: h.owner ?? "—",
          inventoryUom: h.uom ?? "—",
          qcRequired: !!(h.lottracked || h.serialtracked || h.hazmat),
          hazmat: !!h.hazmat,
          lottracked: !!h.lottracked,
          __original: item,
        };
      });
      setTableRows(formatted);
      setTotalCount(
        (Array.isArray(res) ? formatted.length : res?.totalItems ?? res?.totalRecords ?? res?.pagination?.totalRecords) ??
          formatted.length
      );
    } catch (err) {
      console.error(err);
      if (!silent) {
        setTableRows([]);
        setTotalCount(0);
      }
      throw err;
    } finally {
      if (!silent) setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(next);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  useEffect(() => {
    if (!isCreating) loadList(page, pageSize, requestFilters).catch(() => {});
  }, [loadList, isCreating, page, pageSize, requestFilters]);

  useEffect(() => {
    setCategoryOptions((prev) => mergeOptionValues(prev, tableRows, "category"));
    setItemTypeOptions((prev) => mergeOptionValues(prev, tableRows, "itemType"));
    setBrandOptions((prev) => mergeOptionValues(prev, tableRows, "brand"));
  }, [tableRows]);

  const displayRows = useMemo(() => {
    if (filterStatus === "Active") return tableRows.filter((row) => !blockedIds.has(row.id));
    if (filterStatus === "Inactive") return tableRows.filter((row) => blockedIds.has(row.id));
    return tableRows;
  }, [tableRows, filterStatus, blockedIds]);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setFilterCategory("");
    setFilterItemType("");
    setFilterBrand("");
    setFilterStatus("");
    setPage(0);
  };

  const handleDataRefresh = useCallback((payload = {}) => {
    const nextFilters = Array.isArray(payload.filters) ? payload.filters : [];
    const nextSize = Number(payload.pageSize);
    setAppliedFilters(nextFilters);
    if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
    setPage(0);
  }, [pageSize]);

  const filtersActive =
    searchInput.trim() || filterCategory || filterItemType || filterBrand || filterStatus;

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  /**
   * Open the Add-SKU form pre-populated with the selected SKU (read-only for "view").
   * Tries the detail API for the freshest record and falls back to the list row.
   */
  const openSkuForm = async (mode, row) => {
    if (!row) return;
    handleCloseMenu();
    setDetailLoading(true);
    let record = row.__original;
    try {
      const detail = await getSkuDetailsById(row.skuCode);
      if (detail && typeof detail === "object") record = detail;
    } catch {
      // keep the list row as the data source
    } finally {
      setDetailLoading(false);
    }
    setSkuForm({ mode, record });
  };

  const actionColumn = useMemo(
    () => ({
      field: "__actions",
      headerName: "",
      width: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)} aria-label="row actions">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    }),
    []
  );

  const rawColumns = useMemo(
    () => [
      {
        field: "skuCode",
        headerName: "SKU Code",
        width: 150,
        renderCell: (p) => (
          <a
            href={`#/inventory/item/${encodeURIComponent(p.value || "")}`}
            className="text-decoration-none"
            onClick={(e) => e.stopPropagation()}
          >
            {p.value}
          </a>
        ),
      },
      { field: "skuName", headerName: "SKU Name", flex: 1, minWidth: 200 },
      { field: "category", headerName: "Category", width: 130 },
      { field: "brand", headerName: "Brand", width: 120 },
      { field: "inventoryUom", headerName: "Inventory UOM", width: 120 },
      {
        field: "qcRequired",
        headerName: "QC Required",
        width: 120,
        renderCell: (p) =>
          p.row.qcRequired ? (
            <span className="cdt-status cdt-status-warning">Yes</span>
          ) : (
            <span className="cdt-status cdt-status-neutral">No</span>
          ),
      },
      {
        field: "statusUi",
        headerName: "Status",
        width: 110,
        sortable: false,
        renderCell: (p) =>
          blockedIds.has(p.row.id) ? (
            <span className="cdt-status cdt-status-danger">Blocked</span>
          ) : (
            <span className="cdt-status cdt-status-success">Active</span>
          ),
      },
    ],
    [blockedIds]
  );

  const allPossibleColumns = useMemo(() => [actionColumn, ...rawColumns], [actionColumn, rawColumns]);
  const initialVisible = useMemo(() => allPossibleColumns.map((c) => c.field), [allPossibleColumns]);

  if (skuForm) {
    const { mode, record } = skuForm;
    const closeForm = () => setSkuForm(null);
    return (
      <Card className="sku-mgmt-master-list-card border-0 shadow-sm">
        <nav className="px-3 py-2 border-bottom bg-light" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={closeForm}
              >
                Item Management
              </button>
            </li>
            <li className="breadcrumb-item active fw-semibold text-primary" aria-current="page">
              {SKU_FORM_BREADCRUMB[mode]}
            </li>
          </ol>
        </nav>
        <Card.Body>
          <CreateSkuForm
            key={`${mode}-${record?._id || record?.header?._id || record?.header?.skuId || "new"}`}
            mode={mode}
            editData={mode === "create" ? undefined : record}
            readOnly={mode === "view"}
            onClose={closeForm}
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="sku-mgmt-master-list-card sku-list-shell border-0 mb-3">
        <Card.Header className="sku-list-header">
          <div className="sku-list-top">
            <div className="sku-list-title">
              <span className="sku-list-title-icon" aria-hidden>
                <i className="bi bi-box-seam" />
              </span>
              <div>
                <h5>Item Management</h5>
                <p>Inventory &amp; warehouse attributes</p>
              </div>
            </div>

            <div className="sku-list-actions">
              <InputGroup size="sm" className="sku-list-search">
                <span className="input-group-text">
                  <i className="bi bi-search" />
                </span>
                <Form.Control
                  placeholder="Search SKU code…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  aria-label="Search SKU code"
                />
              </InputGroup>
              <Button
                size="sm"
                type="button"
                className="sku-list-btn sku-list-btn-soft"
                onClick={() => toast.info("Import SKU will be available when the feed API is connected.")}
              >
                <i className="bi bi-upload" />
                Import SKU
              </Button>
              <Button
                size="sm"
                className="sku-list-btn sku-list-btn-primary"
                onClick={() => setSkuForm({ mode: "create", record: null })}
              >
                <i className="bi bi-plus-lg" />
                Create SKU
              </Button>
            </div>
          </div>

          <Row className="g-2 mt-1 align-items-end sku-list-filters">
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="small text-secondary mb-0">Category</Form.Label>
              <Form.Select
                size="sm"
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="small text-secondary mb-0">Item Type</Form.Label>
              <Form.Select
                size="sm"
                value={filterItemType}
                onChange={(e) => {
                  setFilterItemType(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All</option>
                {itemTypeOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="small text-secondary mb-0">Brand</Form.Label>
              <Form.Select
                size="sm"
                value={filterBrand}
                onChange={(e) => {
                  setFilterBrand(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All</option>
                {brandOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="small text-secondary mb-0">Status</Form.Label>
              <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Form.Select>
            </Col>
            {filtersActive && (
              <Col xs="auto">
                <Button variant="link" size="sm" className="sku-list-clear" onClick={clearFilters}>
                  <i className="bi bi-x-circle" />
                  Clear filters
                </Button>
              </Col>
            )}
          </Row>
        </Card.Header>
        <Card.Body className="sku-list-body">
          {(!hasLoaded && loading) || detailLoading ? (
            <div className="sku-list-loading">
              <DotLoaderMark />
            </div>
          ) : (
            <CommonDataTable
              allPossibleColumns={allPossibleColumns}
              initialVisible={initialVisible}
              initialRows={displayRows}
              height={520}
              rowsPerPageOptions={[10, 25, 50, 100]}
              exportImportPath="SkuMaster"
              forItems
              forInventory
              showSyncWms
              page={page}
              pageSize={pageSize}
              rowCount={totalCount}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              onDataRefresh={handleDataRefresh}
              onManualRefresh={(payload) =>
                loadList(
                  payload?.page ?? page,
                  payload?.pageSize ?? pageSize,
                  [
                    ...mapSkuUiFiltersToApi(payload?.filters ?? appliedFilters),
                    ...headerFilters,
                  ],
                  { silent: true }
                )
              }
            />
          )}
        </Card.Body>
        <Card.Footer className="sku-list-footer">
          <div className="sku-list-footer-meta">
            Page <strong>{page + 1}</strong> · <strong>{totalCount}</strong> total
          </div>
          <div className="sku-list-pager">
            <button type="button" disabled={page <= 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              <i className="bi bi-chevron-left" />
              Prev
            </button>
            <button type="button" disabled={loading || (page + 1) * pageSize >= totalCount} onClick={() => setPage((p) => p + 1)}>
              Next
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </Card.Footer>
      </Card>

      <Menu open={Boolean(menuAnchorEl)} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={() => openSkuForm("view", menuRow)}>
          <i className="bi bi-eye me-2" /> View
        </MenuItem>
        <MenuItem onClick={() => openSkuForm("edit", menuRow)}>
          <i className="bi bi-pencil me-2" /> Edit
        </MenuItem>
      </Menu>
    </>
  );
}
